import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

// Football : suivi par fixture_id numérique (préfixe "fx_" normalisé).
export const normId = (x) => String(x ?? "").replace(/^fx_/, "");
// Tennis/Basket : suivi par clé "domicile|extérieur" (noms), en minuscules.
const nameOf = (x) => (typeof x === "string" ? x : x?.name) || "";
export const pairKey = (m) => `${nameOf(m?.home)}|${nameOf(m?.away)}`.toLowerCase();

const keyFor = (sport, m) =>
  sport === "foot" ? normId(m?.match_id ?? m?.fixture_id) : pairKey(m);

const FollowsContext = createContext(null);

export const useFollows = () =>
  useContext(FollowsContext) || {
    isFollowed: () => false,
    toggle: async () => {},
    refresh: () => {},
    counts: { foot: 0, tennis: 0, basket: 0 },
    error: false,
  };

export function FollowsProvider({ children }) {
  const { user } = useAuth();
  const [sets, setSets] = useState({ foot: [], tennis: [], basket: [] });
  const [error, setError] = useState(false);

  // Erreur : on garde la liste précédente ; elle n'est remplacée qu'après 3 réponses valides.
  const refresh = useCallback(async () => {
    if (!user) { setSets({ foot: [], tennis: [], basket: [] }); setError(false); return; }
    try {
      const [foot, tennis, basket] = await Promise.all([
        api.get("/live/following").then((r) => (r.data.fixture_ids || []).map(normId)),
        api.get("/tennis/following").then((r) => (r.data.matches || []).map((m) => `${m.home}|${m.away}`.toLowerCase())),
        api.get("/basket/following").then((r) => (r.data.matches || []).map((m) => `${m.home}|${m.away}`.toLowerCase())),
      ]);
      setSets({ foot, tennis, basket });
      setError(false);
    } catch {
      setError(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFollowed = useCallback((sport, m) => {
    const s = sets[sport === "foot" ? "foot" : sport] || [];
    return s.includes(keyFor(sport, m));
  }, [sets]);

  const toggle = useCallback(async (sport, m) => {
    if (!user) return false;
    const bucket = sport === "foot" ? "foot" : sport;
    const key = keyFor(sport, m);
    const currentlyOn = (sets[bucket] || []).includes(key);
    const next = !currentlyOn;
    setSets((cur) => ({
      ...cur,
      [bucket]: next ? Array.from(new Set([...(cur[bucket] || []), key])) : (cur[bucket] || []).filter((x) => x !== key),
    }));
    try {
      if (sport === "foot") {
        await api.post(next ? "/live/follow" : "/live/unfollow", {
          fixture_id: key, home: nameOf(m.home), away: nameOf(m.away), competition: m.competition,
        });
      } else {
        const path = sport === "tennis" ? "/tennis" : "/basket";
        await api.post(next ? `${path}/follow` : `${path}/unfollow`, {
          home: nameOf(m.home), away: nameOf(m.away), tournament: m.tournament || m.competition,
        });
      }
      return next;
    } catch {
      setSets((cur) => ({
        ...cur,
        [bucket]: currentlyOn ? Array.from(new Set([...(cur[bucket] || []), key])) : (cur[bucket] || []).filter((x) => x !== key),
      }));
      return currentlyOn;
    }
  }, [sets, user]);

  const counts = { foot: sets.foot.length, tennis: sets.tennis.length, basket: sets.basket.length };

  return (
    <FollowsContext.Provider value={{ sets, counts, isFollowed, toggle, refresh, error }}>
      {children}
    </FollowsContext.Provider>
  );
}
